import { ParsedUrlQueryInput } from 'querystring'

// https://github.com/sonyged/tart/blob/main/frontend/admin-app/src/hooks/useQueryParams.ts
import { useRouter } from 'next/router'

export type QueryParamCodec<T> = {
  toQueryValue: (v: T) => string
  fromQueryValue: (str: string | undefined) => T
}

export function num(defaultValue = 0): QueryParamCodec<number> {
  return {
    toQueryValue: (v) => v + '',
    fromQueryValue: (s) => (s ? Number(s) : defaultValue),
  }
}

export function str(defaultValue = ''): QueryParamCodec<string> {
  return {
    toQueryValue: (v) => v,
    fromQueryValue: (s) => s ?? defaultValue,
  }
}

export function oneOfStr<T extends string[]>(...values: T): QueryParamCodec<T[number]> {
  return {
    toQueryValue: (v) => v,
    fromQueryValue: (s) => {
      if (!s) return values[0]
      return values.find((v) => v == s) ?? values[0]
    },
  }
}

type Simplify<T> = T extends Function ? T : { [K in keyof T]: T[K] }

type QueryStateFromConfig<P> = {
  [K in keyof P]: P[K] extends QueryParamCodec<infer R> ? R : never
}

export function useQueryParams<P extends Record<string, QueryParamCodec<any>>>(
  config: P
): [
  Simplify<QueryStateFromConfig<P>>,
  (state: Partial<Simplify<QueryStateFromConfig<P>>>, options?: { scroll?: boolean }) => void,
] {
  const router = useRouter()

  let state: any = {}
  for (const name of Object.keys(config)) {
    let s = router.query[name]
    if (Array.isArray(s)) s = undefined
    const v = config[name].fromQueryValue(s)
    state[name] = v
  }

  function update(
    newState: Partial<Simplify<QueryStateFromConfig<P>>>,
    options?: { scroll?: boolean }
  ) {
    const newQuery: any = {}
    for (const name of Object.keys(newState)) {
      const codec = config[name]
      if (!codec) continue
      newQuery[name] = codec.toQueryValue(newState[name])
    }
    const newQueryOption = Object.fromEntries(
      Object.entries({
        ...router.query,
        ...newQuery,
      }).filter(([_, value]) => value !== undefined)
    ) as ParsedUrlQueryInput

    const scroll = options?.scroll ?? true

    router.replace(
      {
        pathname: router.pathname,
        query: newQueryOption,
      },
      undefined,
      { scroll }
    )
  }

  return [state, update]
}
